import { type NFTCollectibleDefinition, useMeFavouritePartner } from '@capsicum/common';
import {
	type LockerRoomCampaignReward,
	type LockerRoomUnlock,
	useOngoingUserParticipationsQuery,
	usePartners
} from '@capsicum/socios-core';
import { useCallback } from 'react';

import { type MyCollectible } from '../models';
import { mapPartnerToCollectiblePartner } from '../util';

/**
 * @returns combined sorted list of collectibles owned by user and LockerRoom participations. Ongoing participations are displayed with an overlay.
 */
export const useMyOngoingParticipationsCollectibles = () => {
	const { getPartnerBy } = usePartners();
	const { favoritePartner } = useMeFavouritePartner();

	return useOngoingUserParticipationsQuery(
		useCallback(
			(data: Array<LockerRoomUnlock> | undefined) =>
				data?.reduce(
					(sortedCollectibles, currentParticipation) => {
						if (currentParticipation?.status === 'accepted') {
							const stackingClosedAt = currentParticipation?.challenge?.campaign?.stackingClosedAt || '';
							const reward: LockerRoomCampaignReward | undefined = currentParticipation.rewards?.[0];
							const partner = mapPartnerToCollectiblePartner(
								getPartnerBy(p => p.id === reward?.reward?.nft?.summary?.partnerId)
							);
							const rewardNft = reward?.reward?.nft as NFTCollectibleDefinition;

							const myCollectible = {
								optionType: currentParticipation.optionType,
								partner,
								summary: {
									...rewardNft.summary,
									collectibleId: reward?.id
								},
								unlockingDate: stackingClosedAt,
								userParticipationId: currentParticipation.id
							} as MyCollectible;

							if (partner && partner.id === favoritePartner?.id) {
								sortedCollectibles.favorite.push(myCollectible);
							} else {
								sortedCollectibles.other.push(myCollectible);
							}
						}

						return sortedCollectibles;
					},
					{
						favorite: [],
						other: []
					} as { favorite: Array<MyCollectible>; other: Array<MyCollectible> }
				),
			[favoritePartner?.id, getPartnerBy]
		),
		{
			direction: 'desc',
			orderBy: 'createdAt',
			'rewardTypes[]': ['nft']
		}
	);
};
